import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Grid, Typography, Box, TextField, Hidden
    , FormControl, InputLabel, Select, Button, Snackbar, Alert
    , Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Search as IconSearch, Block as IconBlock } from "@mui/icons-material";

import comprobantePagoService, { identificadorPago } from "../../services/comprobantePagoService";
import * as catalogoService from "../../services/catalogoService";
import DataGridBase from "../../components/common/DataGridBase";
import { useMantenimiento } from '../../hooks/useMantenimiento';

// Opcion para anular pagos de comprobantes. La busqueda es manual: al abrir la
// pantalla no se muestran resultados y se filtra con el boton de buscar (o
// Enter en un filtro). Solo los pagos activos se pueden anular, y el enlazado
// del codigo del pago abre la ventana con el motivo, el usuario que anula y la
// fecha y hora de la anulacion.

// Catalogo 1 = Estados del pago del comprobante.
const ESTADO_PAGO_ACTIVO = 2;
const ESTADO_PAGO_ANULADO = 4;
const ESTADO_PAGO_CONSOLIDADO = 227;

const today = () => moment().format('yyyy-MM-DD');

const AnularPago = (props) => {
    const { isReadOnly } = useMantenimiento("comprobante");

    // Al abrir la pantalla no se busca: los resultados se piden con el boton.
    const [cargando, setCargando] = useState(false);
    const [results, setResults] = useState([]);
    const [filters, setFilters] = useState({
        // Por defecto se muestran los pagos activos, que son los que se pueden anular.
        idEstado: ESTADO_PAGO_ACTIVO,
        fechaInicio: today(),
        fechaFin: today(),
        serie: "",
        numero: "",
        pagador: "",
    });
    const [listMotivos, setListMotivos] = useState([]);
    const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
    const [idMotivoAnulacion, setIdMotivoAnulacion] = useState(0);

    const [openMessage, setOpenMessage] = useState(false)
    const onCloseMessage = () => { setOpenMessage(!openMessage); }
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const usuario = sessionStorage.getItem('sisdet.usuario') || "";

    // El simbolo de la moneda se muestra junto al monto, igual que en el listado
    // de comprobantes.
    const simboloMoneda = (row) => (row?.moneda || "").toLowerCase().indexOf("dolar") >= 0 ? "US$ " : "S/ ";
    const monto = (row, value) => simboloMoneda(row) + (parseFloat(value) || 0).toFixed(2);

    const fetchResults = useCallback(async () => {
        setCargando(true);

        // Las fechas van en hora local: con toISOString se correrian un dia.
        const fechaInicio = filters.fechaInicio
            ? moment(filters.fechaInicio).startOf("day").format("YYYY-MM-DD HH:mm:ss")
            : null;
        const fechaFin = filters.fechaFin
            ? moment(filters.fechaFin).endOf("day").format("YYYY-MM-DD HH:mm:ss")
            : null;

        const filtersToSend = {
            idEstado: filters.idEstado == null ? 0 : filters.idEstado,
            fechaInicio: fechaInicio,
            fechaFin: fechaFin,
            serie: filters.serie == null ? "" : filters.serie,
            numero: filters.numero == null || filters.numero === "" ? 0 : parseInt(filters.numero),
            pagador: filters.pagador == null ? "" : filters.pagador,
        };

        await comprobantePagoService.getsComprobantePagoSearch(filtersToSend)
            .then((data) => {
                // El grid pide un id unico por fila: el pago se identifica por
                // comprobante y numero de pago.
                setResults((data || []).map((row) => ({ ...row, id: row.idComprobante + "-" + row.numero })));
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err)
            });
    }, [filters])

    const fetchMotivos = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(220)
            .catch((err) => { console.log("Err: ", err) });

        setListMotivos(data || []);
    }, [])

    useEffect(() => {
        fetchMotivos();
    }, [fetchMotivos])

    const columns = [
        // El pago se identifica con la serie y numero del comprobante mas el
        // numero de pago con tres ceros adelante. En los pagos activos este
        // codigo es el enlace que registra la anulacion.
        { field: 'pagoComprobante', headerName: 'Pago', width: 170, sortable: false, headerAlign: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;
                const identificador = identificadorPago(row);

                if (row.idEstado != ESTADO_PAGO_ACTIVO || isReadOnly) return identificador;

                return <Box
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                        setPagoSeleccionado(row);
                        setIdMotivoAnulacion(0);
                    }}
                >
                    {identificador}
                </Box>;
            }
        },
        { field: 'fechaPago', headerName: 'Fecha', width: 135, type: 'date'
            // Con hora y minutos para diferenciar pagos del mismo dia.
            , valueFormatter: (value) => moment(value).format('D/MM/YYYY HH:mm')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estado', headerName: 'Estado', width: 150, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'pagador', headerName: 'Pagador', width: 160, sortable: false, headerAlign: 'left' },
        { field: 'monto', headerName: 'Total', width: 110, sortable: false, headerAlign: 'right', align: 'right'
            , renderCell: (cellValues) => monto(cellValues.row, cellValues.row.monto)
        },
        { field: 'montoSaldo', headerName: 'Saldo', width: 110, sortable: false, headerAlign: 'right', align: 'right'
            , renderCell: (cellValues) => monto(cellValues.row, cellValues.row.montoSaldo)
        },
        { field: 'motivoAnulacion', headerName: 'Motivo', width: 180, sortable: false, headerAlign: 'left' },
        { field: 'fechaAnulacion', headerName: 'Fecha Anulacion', width: 140, type: 'date'
            , valueFormatter: (value) => value ? moment(value).format('D/MM/YYYY HH:mm') : ''
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'usuarioAnulacion', headerName: 'Anulado Por', width: 100, sortable: false, headerAlign: 'center' },
    ];

    const anularPago = async () => {
        if (!pagoSeleccionado || idMotivoAnulacion <= 0) return;

        await comprobantePagoService.anularComprobantePago(
            pagoSeleccionado.idComprobante, pagoSeleccionado.numero, idMotivoAnulacion)
            .then(() => {
                setPropsMessage({ severity: "success", message: "Se anularo el pago con exito" })
                setOpenMessage(true);
                setPagoSeleccionado(null);
                setIdMotivoAnulacion(0);
                fetchResults();
            })
            .catch(() => {
                setPropsMessage({ severity: "error", message: "Hubo un error" })
                setOpenMessage(true);
            });
    }

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Anular Pago
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1}>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="fechaInicio"
                                    label="Fecha Desde"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={filters.fechaInicio == null ? "" : moment(filters.fechaInicio).format('yyyy-MM-DD')}
                                    onChange={(event) => setFilters({ ...filters, fechaInicio: event.target.value })}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="fechaFin"
                                    label="Fecha Hasta"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={filters.fechaFin == null ? "" : moment(filters.fechaFin).format('yyyy-MM-DD')}
                                    onChange={(event) => setFilters({ ...filters, fechaFin: event.target.value })}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopago">Estado del Pago</InputLabel>
                                    <Select
                                        native
                                        label="Estado del Pago"
                                        value={filters.idEstado}
                                        onChange={(event) => setFilters({ ...filters, idEstado: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstado', id: 'input-estadopago' }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        <option key={ESTADO_PAGO_ACTIVO} value={ESTADO_PAGO_ACTIVO}>{"Activo"}</option>
                                        <option key={ESTADO_PAGO_ANULADO} value={ESTADO_PAGO_ANULADO}>{"Anulado"}</option>
                                        <option key={ESTADO_PAGO_CONSOLIDADO} value={ESTADO_PAGO_CONSOLIDADO}>{"Pago Consolidado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="serie"
                                    label="Serie"
                                    fullWidth
                                    value={filters.serie}
                                    onChange={(event) => setFilters({ ...filters, serie: event.target.value })}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="numero"
                                    label="Numero"
                                    type="number"
                                    fullWidth
                                    value={filters.numero}
                                    onChange={(event) => setFilters({ ...filters, numero: event.target.value })}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="pagador"
                                    label="Pagador"
                                    fullWidth
                                    value={filters.pagador}
                                    onChange={(event) => setFilters({ ...filters, pagador: event.target.value })}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            fetchResults();
                                        }
                                    }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={2} sm={2} display={"flex"} alignItems={"center"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { fetchResults() }}>
                                    {<Hidden smDown></Hidden>}
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                disabled={isReadOnly}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <Dialog open={pagoSeleccionado != null} onClose={() => { setPagoSeleccionado(null) }} fullWidth maxWidth="xs">
                <DialogTitle>
                    <Typography variant="h6" component="span">
                        <Box sx={{ fontWeight: 'bold' }}>
                            Anular Pago
                        </Box>
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems="center" sx={{ paddingTop: "12px" }}>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                disabled
                                name="pagoComprobante"
                                label="Pago"
                                fullWidth
                                value={identificadorPago(pagoSeleccionado)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                disabled
                                name="monto"
                                label="Total"
                                type="number"
                                inputProps={{ step: 0.01 }}
                                fullWidth
                                value={pagoSeleccionado ? (parseFloat(pagoSeleccionado.monto) || 0).toFixed(2) : 0}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <FormControl fullWidth required>
                                <InputLabel htmlFor="input-motivo">Motivo</InputLabel>
                                <Select
                                    native
                                    label="Motivo"
                                    value={idMotivoAnulacion}
                                    onChange={(event) => setIdMotivoAnulacion(parseInt(event.target.value))}
                                    inputProps={{ name: 'idMotivoAnulacion', id: 'input-motivo' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Seleccione)"}</option>
                                    {listMotivos.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                disabled
                                name="fechaAnulacion"
                                label="Fecha y Hora"
                                fullWidth
                                value={moment().format('DD/MM/YYYY HH:mm')}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <TextField
                                disabled
                                name="usuarioAnulacion"
                                label="Usuario que Anula"
                                fullWidth
                                value={usuario}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPagoSeleccionado(null) }}>Cancelar</Button>
                    <Button variant="contained" startIcon={<IconBlock />} onClick={anularPago} disabled={idMotivoAnulacion <= 0}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={onCloseMessage}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={onCloseMessage} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default AnularPago;